import React from "react";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import { formatMoney, convertToTitleCase } from "../../../../utils";
import moment from "moment";
import { useUser } from "../../../../hooks";

/** Date-only payment_date parses as midnight; prefer created_at for clock time. */
function formatTransactionDateTime(transaction) {
  const calendarSrc =
    transaction?.payment_date ||
    transaction?.created_at ||
    transaction?.date;
  const clockSrc =
    transaction?.created_at ||
    transaction?.payment_date ||
    transaction?.date;
  if (!calendarSrc && !clockSrc) return "N/A";
  const datePart = moment(calendarSrc || clockSrc).format("DD-MMM-YYYY");
  const timePart = moment(clockSrc || calendarSrc).format("hh:mm A");
  return `${datePart} • ${timePart}`;
}

const TransactionRow = ({ transaction, isLast }) => {
  const user = useUser();

  // Get transaction type/description
  const getTransactionType = () => {
    // For contributions, always show "Contribution" as the type
    if (transaction?.type === "contribution" || transaction?.contribution_category || transaction?.category) {
      return "Contribution";
    }
    if (transaction?.type) {
      return convertToTitleCase(transaction.type);
    }
    if (transaction?.loan_type) {
      return `Loan - ${convertToTitleCase(transaction.loan_type)}`;
    }
    if (transaction?.description) {
      return transaction.description;
    }
    return "Transaction";
  };

  // Get transaction icon based on type
  const getTransactionIcon = () => {
    const type = (transaction?.type || "").toLowerCase();
    const categoryName = (transaction?.contribution_category?.name || transaction?.category?.name || "").toLowerCase();
    
    // Use different icons for different contribution types
    if (type.includes("contribution") || categoryName) {
      // Cycle through recent icons based on transaction ID for variety
      const iconIndex = (transaction?.id || 0) % 3;
      const icons = ["/MemberSide/Icons/recent1.svg", "/MemberSide/Icons/recent2.svg", "/MemberSide/Icons/recent3.svg"];
      return icons[iconIndex] || "/MemberSide/Icons/recent1.svg";
    }
    if (type.includes("loan") || type.includes("repayment")) {
      return "/MemberSide/Icons/dashboard1.svg";
    }
    return "/MemberSide/Icons/recent1.svg";
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const statusUpper = (status || "").toUpperCase();
    if (statusUpper === "SUCCESSFUL" || statusUpper === "PROCESSED" || statusUpper === "PAID") {
      return "#19BC31"; // Green
    }
    if (statusUpper === "PENDING") {
      return "#FFA500"; // Orange
    }
    if (statusUpper === "FAILED" || statusUpper === "DECLINED") {
      return "#FF4343"; // Red
    }
    return "#A1A0A0"; // Grey
  };

  const status = transaction?.status || "PENDING";
  const statusColor = getStatusColor(status);
  const transactionType = getTransactionType();
  const amount = parseFloat(transaction?.amount || 0);
  const hasDateDisplay =
    transaction?.payment_date ||
    transaction?.created_at ||
    transaction?.date;
  const userPhoto = user?.photo || transaction?.memberImage || "/Staff/avatar.svg";

  return (
    <div
      className={`d-flex justify-content-between align-items-center py-3 px-3 ${
        !isLast && "border-bottom"
      }`}
      style={{ borderColor: "#e0e0e0" }}
    >
      {/* Left Section - Icon and Transaction Info */}
      <div className="d-flex align-items-center gap-3 flex-1 min-w-0">
        {/* Transaction Icon */}
        <div className="flex-shrink-0">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: "40px",
              height: "40px",
              backgroundColor: "#f7f7f7",
              border: "1px solid #e0e0e0",
            }}
          >
            <FallbackImage
              src={getTransactionIcon()}
              width="24px"
              height="24px"
              style={{ objectFit: "contain" }}
            />
          </div>
        </div>

        {/* Transaction Details */}
        <div className="flex-1 min-w-0">
          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
            <p
              className="fw-semibold text-black1 mb-0 text-truncate"
              style={{ fontSize: "14px", maxWidth: "200px" }}
              title={transactionType}
            >
              {transactionType}
            </p>
            {/* Status Badge */}
            <span
              className="px-2 py-1 rounded-pill text-white fw-semibold flex-shrink-0"
              style={{
                backgroundColor: statusColor,
                fontSize: "11px",
                minWidth: "fit-content",
              }}
            >
              {convertToTitleCase(status)}
            </span>
          </div>
          {hasDateDisplay && (
            <div className="d-flex align-items-center">
              <span className="text-muted small" style={{ fontSize: "12px" }}>
                {formatTransactionDateTime(transaction)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Right Section - Amount */}
      <div className="text-end flex-shrink-0" style={{ minWidth: "120px" }}>
        <p className="fw-bold text-black1 mb-0" style={{ fontSize: "16px", whiteSpace: "nowrap" }}>
          {formatMoney(amount, true, "NGN")}
        </p>
        {transaction?.payment_method && (
          <p className="text-muted small mb-0" style={{ fontSize: "11px" }}>
            {(() => {
              const method = (transaction.payment_method || "").toUpperCase();
              // Map payment methods to user-friendly names
              if (method.includes("PAYSTACK") || method.includes("CARD")) {
                return "Card";
              }
              if (method.includes("BANK_TRANSFER") || method.includes("BANK TRANSFER")) {
                return "Bank Transfer";
              }
              if (method.includes("CASH")) {
                return "Cash";
              }
              if (method.includes("CHEQUE")) {
                return "Cheque";
              }
              // Default: format the method name
              return convertToTitleCase(transaction.payment_method.replace(/_/g, " "));
            })()}
          </p>
        )}
      </div>
    </div>
  );
};

export default TransactionRow;

